let loading;
const LOAD_ERROR = 'The security check could not load. Please try again.';
const VERIFY_ERROR = 'The security check is unavailable. Please try again or contact us directly.';
const RETRY_DELAYS = [1000, 3000];
// Only transient provider failures are retried. Bad keys, hostnames and unsupported browsers need attention.
const retryable = code => /^(?:110600|110620|200500|300\d{3}|600\d{3})$/.test(String(code));

export function loadTurnstile({ document: doc = globalThis.document, window: win = globalThis.window, nonce } = {}) {
  if (win?.turnstile) return Promise.resolve(win.turnstile);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const script = doc.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    if (nonce) script.nonce = nonce;
    let settled = false;
    const fail = () => { if (settled) return; settled = true; clearTimeout(timer); script.onload = script.onerror = null; script.remove(); loading = undefined; reject(new Error(LOAD_ERROR)); };
    const timer = setTimeout(fail, 15000);
    script.onload = () => {
      if (settled) return;
      if (!win.turnstile) { fail(); return; }
      settled = true; clearTimeout(timer); script.onload = script.onerror = null;
      resolve(win.turnstile);
    };
    script.onerror = fail;
    doc.head.appendChild(script);
  });
  return loading;
}

export async function mountTurnstile(container, { siteKey, action = 'lead', onToken = () => {}, onError = () => {}, onPending = () => {}, nonce, api, signal } = {}) {
  if (!container || !siteKey) throw new Error('A container and public site key are required.');
  let token = '', active = true, retryTimer, retries = 0;
  const live = () => active && !signal?.aborted;
  const cancelRetry = () => { clearTimeout(retryTimer); retryTimer = undefined; };
  const clear = () => { token = ''; if (live()) onToken(''); };
  const pending = () => { if (live()) { clear(); onError(''); onPending(true); } };
  const fail = message => { if (live()) { cancelRetry(); clear(); onPending(false); onError(message); } };
  pending();
  let provider;
  try { provider = await (api || loadTurnstile({ nonce })); }
  catch (error) { fail(error.message); throw error; }
  // A cancelled StrictMode mount must not create a widget that its late cleanup would remove from the new mount.
  if (!live()) return { getToken: () => '', reset() {}, destroy() {} };
  let id;
  const resetProvider = () => { try { provider.reset(id); } catch { fail(VERIFY_ERROR); } };
  const recover = code => {
    if (!live()) return true;
    clear();
    if (retryTimer !== undefined) return true;
    if (!retryable(code) || retries >= RETRY_DELAYS.length) { fail(VERIFY_ERROR); return true; }
    pending();
    retryTimer = setTimeout(() => { retryTimer = undefined; if (live()) resetProvider(); }, RETRY_DELAYS[retries++]);
    return true;
  };
  try {
    id = provider.render(container, {
      sitekey: siteKey, action, theme: 'auto', size: 'flexible', appearance: 'interaction-only',
      'response-field': true, 'response-field-name': 'cf-turnstile-response',
      // Own the retry budget instead of overlapping resets with the SDK's unbounded automatic retries.
      retry: 'never', 'refresh-expired': 'auto', 'refresh-timeout': 'manual',
      callback: value => { if (live()) { cancelRetry(); retries = 0; token = value; onPending(false); onError(''); onToken(value); } },
      'expired-callback': () => { if (live()) { cancelRetry(); retries = 0; pending(); } },
      'timeout-callback': () => recover('110620'),
      'error-callback': recover,
      'unsupported-callback': () => fail('Please use an up-to-date browser for the security check, or contact us directly.'),
    });
  } catch (error) { fail(VERIFY_ERROR); throw error; }
  signal?.addEventListener('abort', cancelRetry, { once: true });
  return {
    getToken: () => token,
    reset() { if (live()) { cancelRetry(); retries = 0; pending(); resetProvider(); } },
    destroy() { if (active) { cancelRetry(); signal?.removeEventListener('abort', cancelRetry); clear(); active = false; provider.remove(id); } },
  };
}
